import { readingTarget, rowsAround } from '@/features/ai-changeset/utils/changeset-view';
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

const STEP_SETTLE_MS = 700;
const READING_LINE_OFFSET = 4;

const targetSelector = (id: string) => `[data-quick-edit-target="${id}"]`;

function findScroller(pageName: string): HTMLElement | null {
  const page = `.page[data-name="${pageName}"]`;
  const current = document.querySelector<HTMLElement>(`${page}.page-current .page-content`);
  if (current) return current;
  return (
    document
      .querySelector<HTMLElement>(`${page} [data-quick-edit-target]`)
      ?.closest<HTMLElement>('.page-content') ?? null
  );
}

export interface ChangePulse {
  id: string;
  seq: number;
}

interface NavigationState {
  currentId: string | null;
  pulse: ChangePulse | null;
}

export interface NavigationStore {
  get: () => NavigationState;
  set: (next: Partial<NavigationState>) => void;
  subscribe: (listener: () => void) => () => void;
}

function createNavigationStore(): NavigationStore {
  let state: NavigationState = { currentId: null, pulse: null };
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set: (next) => {
      const merged = { ...state, ...next };
      if (merged.currentId === state.currentId && merged.pulse === state.pulse) return;
      state = merged;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const DOCK_GAP = 12;

function visibleBand(scroller: HTMLElement): { top: number; bottom: number } {
  const box = scroller.getBoundingClientRect();
  const top = box.top + (parseFloat(getComputedStyle(scroller).paddingTop) || 0);
  const dock = scroller.closest('.page')?.querySelector('.ai-dock--floating');
  const dockTop = dock ? dock.getBoundingClientRect().top - DOCK_GAP : box.bottom;
  return { top, bottom: Math.max(top, Math.min(box.bottom, dockTop)) };
}

function readingLine(scroller: HTMLElement): number {
  const band = visibleBand(scroller);
  return band.top + (band.bottom - band.top) / 3;
}

export function revealOffset(
  row: { top: number; bottom: number },
  band: { top: number; bottom: number },
  line: number,
): number {
  const offset = row.top - line;
  const overflow = row.bottom - offset - band.bottom;
  const shifted = overflow > 0 ? offset + overflow : offset;
  return Math.min(shifted, row.top - band.top);
}

export function rowsAtReadingLine(pageName: string, count: number): string[] {
  const scroller = findScroller(pageName);
  if (!scroller) return [];
  const box = scroller.getBoundingClientRect();
  const top = box.top + (parseFloat(getComputedStyle(scroller).paddingTop) || 0);
  const rows = [...scroller.querySelectorAll<HTMLElement>('[data-view-row]')].flatMap((el) => {
    const rect = el.getBoundingClientRect();
    const id = el.dataset['viewRow'];
    return id && rect.bottom > top && rect.top < box.bottom
      ? [{ id, top: rect.top, bottom: rect.bottom }]
      : [];
  });
  return rowsAround(rows, readingLine(scroller), count);
}

export function useChangeNavigation(ids: string[], layoutKey: string, pageName: string) {
  const [store] = useState(createNavigationStore);
  const steppedAt = useRef(0);
  const pinned = useRef<string | null>(null);
  const held = useRef<{ id: string; top: number } | null>(null);
  const idsKey = ids.join(',');

  useLayoutEffect(() => {
    const anchor = held.current;
    held.current = null;
    const scroller = anchor && findScroller(pageName);
    const el = anchor && scroller?.querySelector(targetSelector(anchor.id));
    if (!anchor || !scroller || !el) return;
    scroller.scrollTop += el.getBoundingClientRect().top - anchor.top;
  }, [layoutKey, pageName]);

  useEffect(() => {
    const ordered = idsKey ? idsKey.split(',') : [];
    store.set({ currentId: null, pulse: null });
    if (ordered.length === 0) return;
    let scroller: HTMLElement | null = null;
    let raf = 0;
    let attempts = 0;
    const compute = () => {
      raf = 0;
      if (!scroller || Date.now() - steppedAt.current < STEP_SETTLE_MS) return;
      const atEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      const pinnedEl = pinned.current && scroller.querySelector(targetSelector(pinned.current));
      if (
        atEnd &&
        pinnedEl &&
        pinnedEl.getBoundingClientRect().top < scroller.getBoundingClientRect().bottom
      ) {
        return;
      }
      pinned.current = null;
      const view = scroller;
      const rows = ordered.flatMap((id) => {
        const el = view.querySelector(targetSelector(id));
        return el ? [{ id, top: el.getBoundingClientRect().top }] : [];
      });
      store.set({ currentId: readingTarget(rows, readingLine(view)) });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    const attach = () => {
      raf = 0;
      scroller = findScroller(pageName);
      if (scroller) {
        scroller.addEventListener('scroll', onScroll, { passive: true });
        compute();
      } else if (attempts++ < 30) {
        raf = requestAnimationFrame(attach);
      }
    };
    attach();
    return () => {
      scroller?.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [idsKey, pageName, store]);

  const jumpTo = (id: string) => {
    if (!ids.includes(id)) return;
    steppedAt.current = Date.now();
    pinned.current = id;
    const previous = store.get().pulse;
    store.set({ currentId: id, pulse: { id, seq: (previous?.seq ?? 0) + 1 } });
    const scroller = findScroller(pageName);
    const el = scroller?.querySelector(targetSelector(id));
    if (!scroller || !el) return;
    const rect = el.getBoundingClientRect();
    const offset = revealOffset(
      rect,
      visibleBand(scroller),
      readingLine(scroller) - READING_LINE_OFFSET,
    );
    scroller.scrollTo({ top: scroller.scrollTop + offset, behavior: 'smooth' });
  };

  const holdPosition = () => {
    const { currentId } = store.get();
    const scroller = findScroller(pageName);
    const el = currentId && scroller?.querySelector(targetSelector(currentId));
    if (!currentId || !scroller || !el) return;
    const top = el.getBoundingClientRect().top;
    const box = scroller.getBoundingClientRect();
    held.current = top >= box.top && top < box.bottom ? { id: currentId, top } : null;
  };

  const step = (delta: 1 | -1) => {
    const { currentId } = store.get();
    const index = currentId ? ids.indexOf(currentId) : -1;
    const target = ids.at(index < 0 ? (delta === 1 ? 0 : -1) : index + delta);
    if (target) jumpTo(target);
  };

  return { store, ids, step, jumpTo, holdPosition };
}

export type ChangeNavigation = ReturnType<typeof useChangeNavigation>;

export function useNavigationPosition(navigation: ChangeNavigation) {
  const currentId = useSyncExternalStore(
    navigation.store.subscribe,
    () => navigation.store.get().currentId,
  );
  const index = currentId ? navigation.ids.indexOf(currentId) : -1;
  return {
    position: index >= 0 ? index + 1 : null,
    total: navigation.ids.length,
    canPrev: index > 0,
    canNext: index < navigation.ids.length - 1,
  };
}

export function usePulse(store: NavigationStore, id: string): number {
  return useSyncExternalStore(store.subscribe, () => {
    const pulse = store.get().pulse;
    return pulse?.id === id ? pulse.seq : 0;
  });
}
